import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@/app/hooks';
import { useGetMenuQuery } from '@/app/api/resourceApi';
import { formatPrice } from '@/components/ui/Price';
import DishCodeTag from '@/features/menu/DishCodeTag';
import { categoryColor } from '@/features/menu/categoryColor';
import PendingRoundsSheet from '@/features/waiter/PendingRoundsSheet';
import { pendingActionErrorKey, useTakeOverRound } from '@/features/waiter/useTakeOverRound';
import {
  useGetSessionDetailQuery,
  useDecrementItemMutation,
  useValidateOrderMutation,
  useRefuseOrderMutation,
  useFireRoundMutation,
  waiterApi,
  type PendingValidationRound,
  type WaiterOrder,
  type WaiterOrderItem,
} from '@/features/waiter/waiterApi';
import {
  groupByCategory,
  commandesLineMergeKey,
  isCommandesLineLocked,
  isRoundPendingValidation,
  liveHeldRounds,
} from './commandesGrouping';
import { useModalBackClose } from './useModalBackClose';
import TicketFireConfirm from './TicketFireConfirm';
import type { ShowWaiterToast } from './waiterToast';

export interface TicketCommandesProps {
  hidden: boolean;
  sessionUuid: string;
  restaurantUuid: string;
  /** Waiter JWT — every call here (`sessions/<uuid>/`, `decrement/`,
   *  `validate/`, `refuse/`) uses it, never the note's guest token. */
  token: string;
  /** Table number or Emporter label — only for the reused
   *  `PendingRoundsSheet`'s title, unchanged from its `TablesOverview` use. */
  label: string;
  showToast: ShowWaiterToast;
  /** DF-0223 — compteur incremente par `TicketPage` quand on arrive ici par la
   *  pastille rouge d'une tuile : la feuille des rondes s'ouvre d'elle-meme.
   *  Un compteur et non un booleen, pour qu'un deuxieme appui la rouvre apres
   *  que le serveur l'a refermee. */
  openPendingSheetRequest?: number;
}

const OTHER_CATEGORY_KEY = '__other__';

interface FlatItem extends WaiterOrderItem {
  orderUuid: string;
  onlineStatus: string;
  paymentStatus: string;
  caisseHandoffStatus: string;
  lastValidatedRoundNumber: number | null;
}

/** One display line ⇔ items sharing `commandesLineMergeKey` (W-T-61) — the
 *  common case is exactly one `WaiterOrderItem` (the backend already merges
 *  identical lines within a round into one row with a `qte`); this only
 *  matters if the same (product, options, comment, round) combination ever
 *  arrives as two separate rows. */
interface MergedLine {
  key: string;
  items: FlatItem[];
}

function mergeLines(items: FlatItem[]): MergedLine[] {
  const byKey = new Map<string, FlatItem[]>();
  for (const item of items) {
    const key = commandesLineMergeKey(item);
    const bucket = byKey.get(key);
    if (bucket) bucket.push(item);
    else byKey.set(key, [item]);
  }
  return [...byKey.entries()].map(([key, groupItems]) => ({ key, items: groupItems }));
}

/**
 * 04-ux-mobile-spec.md §2.6, 06-test-cases.md W-T-59..69 — « Total : X € »,
 * blocks per category (menu order, an « Autres » block last for a
 * deleted/renamed category), lines merged per round, red « − » with confirm,
 * pending rounds via the EXISTING `PendingRoundsSheet`. Reads its own
 * `getSessionDetail` subscription — the exact same query args as
 * `TicketPage`'s, so RTK Query shares one cache entry and one 5s poll
 * instead of this component running a second one.
 */
export default function TicketCommandes({
  hidden,
  sessionUuid,
  restaurantUuid,
  token,
  label,
  showToast,
  openPendingSheetRequest = 0,
}: TicketCommandesProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { data: sessionDetail } = useGetSessionDetailQuery({ sessionUuid, token }, { skip: !sessionUuid || !token });
  const { data: menu } = useGetMenuQuery(restaurantUuid, { skip: !restaurantUuid });
  const [decrementItem] = useDecrementItemMutation();
  const [validateOrder] = useValidateOrderMutation();
  const [refuseOrder] = useRefuseOrderMutation();
  const takeOverRound = useTakeOverRound(restaurantUuid, token);
  const [fireRound] = useFireRoundMutation();

  const [decrementTarget, setDecrementTarget] = useState<{
    orderUuid: string;
    itemUuid: string;
    name: string;
  } | null>(null);
  const [decrementSubmitting, setDecrementSubmitting] = useState(false);

  const [pendingSheetOpen, setPendingSheetOpen] = useState(false);
  const [pendingBusyOrderUuid, setPendingBusyOrderUuid] = useState<string | null>(null);
  const [pendingActionError, setPendingActionError] = useState<string | null>(null);

  // SC-902 — T7: « Lancer » a held service round to the kitchen (design
  // §1.6/§1.7). Parent-held busy/submitting state, one mutation, invalidate
  // `SessionDetail` on success — same idiom as the pending-rounds Valider
  // above (`useFireRoundMutation` already invalidates on success).
  const [fireTarget, setFireTarget] = useState<{
    orderUuid: string;
    round: number;
    label: string;
    dishCount: number;
  } | null>(null);
  const [fireSubmitting, setFireSubmitting] = useState(false);
  const [fireBusyRound, setFireBusyRound] = useState<number | null>(null);

  useModalBackClose('decrement-confirm', () => setDecrementTarget(null));

  useEffect(() => {
    if (openPendingSheetRequest > 0) setPendingSheetOpen(true);
  }, [openPendingSheetRequest]);

  const categoriesById = useMemo(() => {
    const map = new Map<string, { id: string; name: string; color?: string }>();
    for (const cat of menu?.categories ?? []) map.set(cat.id, cat);
    return map;
  }, [menu]);
  const categoryOrder = useMemo(
    () => [...(menu?.categories ?? [])].sort((a, b) => a.order - b.order).map((c) => c.id),
    [menu],
  );
  // D4 — the waiter-mode session-detail payload (sale_api
  // `_session_detail_item_payload`) does not send a `category` field at all;
  // `item.category` is always undefined at runtime, which used to send
  // every line to « Autres ». The category is resolved the same way the
  // Plats rail resolves it: productUuid → the menu item → its categoryId.
  // A product no longer in the menu (deleted/renamed since the order was
  // placed) is the only case that legitimately falls back to « Autres ».
  const categoryIdByProduct = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of menu?.items ?? []) map.set(item.id, item.categoryId);
    return map;
  }, [menu]);

  const flatItems: FlatItem[] = useMemo(() => {
    const orders = sessionDetail?.orders ?? [];
    return orders
      .filter((o) => o.onlineStatus !== 'cancelled')
      .flatMap((o) =>
        o.items.map((item) => ({
          ...item,
          category: categoryIdByProduct.get(item.productUuid) ?? OTHER_CATEGORY_KEY,
          orderUuid: o.uuid,
          onlineStatus: o.onlineStatus,
          paymentStatus: o.paymentStatus,
          caisseHandoffStatus: o.caisseHandoffStatus,
          lastValidatedRoundNumber: o.lastValidatedRoundNumber,
        })),
      );
  }, [sessionDetail, categoryIdByProduct]);

  // D4 — cart-scope question answers (`isCartOption`) are never dish lines:
  // they never carry a category/round chip and render in their own
  // « Questions » block, after every category, instead of contaminating
  // « Autres » with the platform's own generated lines.
  const dishItems = useMemo(() => flatItems.filter((i) => !i.isCartOption), [flatItems]);
  const questionItems = useMemo(() => flatItems.filter((i) => i.isCartOption), [flatItems]);

  const mergedLines = useMemo(() => mergeLines(dishItems), [dishItems]);
  const mergedQuestionLines = useMemo(() => mergeLines(questionItems), [questionItems]);

  const categoryBlocks = useMemo(() => {
    const withCategory = mergedLines.map((line) => ({ ...line, category: line.items[0].category }));
    return groupByCategory(withCategory, categoryOrder);
  }, [mergedLines, categoryOrder]);

  const pendingRounds = sessionDetail?.session.pendingValidation ?? [];

  // SC-902 — T7: one row per LIVE held round (design §1.7), across every
  // order of the session (a table merges its rounds into one anchor in
  // practice, but nothing here assumes exactly one). `liveHeldRounds`
  // already excludes a cancelled order's rounds and a round emptied down to
  // only cart-option lines (F2/§1.6). This depends on DATA only
  // (`order.heldRounds`), never the config flag — spec §1.10 G.
  const heldRoundRows = useMemo(() => {
    const orders: WaiterOrder[] = sessionDetail?.orders ?? [];
    const rows: { orderUuid: string; round: number; label: string; dishCount: number }[] = [];
    for (const order of orders) {
      for (const round of liveHeldRounds(order)) {
        const dishCount = order.items
          .filter((i) => i.roundNumber === round && !i.isCartOption)
          .reduce((sum, i) => sum + i.qte, 0);
        rows.push({ orderUuid: order.uuid, round, label: order.roundServiceLabels[String(round)] ?? '', dishCount });
      }
    }
    return rows.sort((a, b) => a.round - b.round);
  }, [sessionDetail]);

  // Per-order set of live held rounds, so a Commandes line can tell whether
  // ITS round is one of them (the amber « En attente d'envoi » chip).
  const heldRoundsByOrder = useMemo(() => {
    const map = new Map<string, Set<number>>();
    for (const order of sessionDetail?.orders ?? []) {
      map.set(order.uuid, new Set(liveHeldRounds(order)));
    }
    return map;
  }, [sessionDetail]);

  function heldRoundName(round: number, label: string): string {
    return label || t('waiter.ticket.commandes.heldRound', { n: round });
  }

  async function confirmFire() {
    const target = fireTarget;
    if (!target || fireSubmitting) return;
    setFireSubmitting(true);
    setFireBusyRound(target.round);
    try {
      await fireRound({ orderUuid: target.orderUuid, roundNumber: target.round, sessionUuid, token }).unwrap();
      showToast(t('waiter.ticket.commandes.fireDone', { name: heldRoundName(target.round, target.label) }));
      setFireTarget(null);
    } catch (err) {
      const code = (err as { data?: { code?: string } } | undefined)?.data?.code;
      if (code === 'PENDING_ROUND_EXISTS') {
        showToast(t('waiter.ticket.commandes.fireErrors.pendingRoundExists'));
        dispatch(waiterApi.util.invalidateTags([{ type: 'SessionDetail', id: sessionUuid }]));
        setFireTarget(null);
        setPendingSheetOpen(true);
      } else if (code === 'SESSION_NOT_OPEN') {
        showToast(t('waiter.ticket.commandes.fireErrors.sessionNotOpen'));
        dispatch(waiterApi.util.invalidateTags([{ type: 'SessionDetail', id: sessionUuid }]));
        setFireTarget(null);
      } else if (code === 'ORDER_LOCKED') {
        showToast(t('waiter.ticket.commandes.fireErrors.orderLocked'));
        setFireTarget(null);
      } else if (code === 'ROUND_NOT_FOUND') {
        showToast(t('waiter.ticket.commandes.fireErrors.roundNotFound'));
        dispatch(waiterApi.util.invalidateTags([{ type: 'SessionDetail', id: sessionUuid }]));
        setFireTarget(null);
      } else {
        showToast(t('waiter.ticket.commandes.fireErrors.generic'));
        setFireTarget(null);
      }
    } finally {
      setFireSubmitting(false);
      setFireBusyRound(null);
    }
  }

  function openDecrementConfirm(line: MergedLine) {
    const first = line.items[0];
    setDecrementTarget({ orderUuid: first.orderUuid, itemUuid: first.uuid, name: first.name });
  }

  async function confirmDecrement() {
    const target = decrementTarget;
    if (!target || decrementSubmitting) return;
    setDecrementSubmitting(true);
    try {
      const result = await decrementItem({ orderUuid: target.orderUuid, itemUuid: target.itemUuid, token }).unwrap();
      dispatch(
        waiterApi.util.updateQueryData('getSessionDetail', { sessionUuid, token }, (draft) => {
          const idx = draft.orders.findIndex((o) => o.uuid === result.order.uuid);
          if (idx === -1) draft.orders.push(result.order);
          else draft.orders[idx] = result.order;
        }),
      );
      setDecrementTarget(null);
    } catch (err) {
      const code = (err as { data?: { reason?: string } } | undefined)?.data?.reason;
      const reasonKey: Record<string, string> = {
        paid: 'waiter.ticket.errors.itemLockedPaid',
        handed_over: 'waiter.ticket.errors.itemLockedHandedOver',
        cancelled: 'waiter.ticket.errors.itemLockedCancelled',
        handed_off: 'waiter.ticket.errors.itemLockedHandedOff',
        pending: 'waiter.ticket.errors.itemLockedPending',
        session_closed: 'waiter.ticket.errors.itemLockedSessionClosed',
      };
      showToast(t(code && reasonKey[code] ? reasonKey[code] : 'waiter.ticket.errors.generic'));
      dispatch(waiterApi.util.invalidateTags([{ type: 'SessionDetail', id: sessionUuid }]));
      setDecrementTarget(null);
    } finally {
      setDecrementSubmitting(false);
    }
  }

  async function handleValidate(round: PendingValidationRound) {
    setPendingBusyOrderUuid(round.orderUuid);
    setPendingActionError(null);
    try {
      await validateOrder({ orderUuid: round.orderUuid, roundNumber: round.roundNumber, token }).unwrap();
    } catch (err) {
      setPendingActionError(t(pendingActionErrorKey(err)));
    } finally {
      dispatch(waiterApi.util.invalidateTags([{ type: 'SessionDetail', id: sessionUuid }]));
      setPendingBusyOrderUuid(null);
    }
  }

  async function handleRefuse(round: PendingValidationRound) {
    setPendingBusyOrderUuid(round.orderUuid);
    setPendingActionError(null);
    try {
      await refuseOrder({ orderUuid: round.orderUuid, roundNumber: round.roundNumber, token }).unwrap();
    } catch (err) {
      setPendingActionError(t(pendingActionErrorKey(err)));
    } finally {
      dispatch(waiterApi.util.invalidateTags([{ type: 'SessionDetail', id: sessionUuid }]));
      setPendingBusyOrderUuid(null);
    }
  }

  // SC-953 — « Modifier » : the round's dishes land in this ticket's cart and
  // `TicketPage` opens it (location state set by `useTakeOverRound`).
  async function handleModify(round: PendingValidationRound) {
    setPendingBusyOrderUuid(round.orderUuid);
    setPendingActionError(null);
    try {
      await takeOverRound(round, sessionDetail?.session.mode === 'buffet');
      setPendingSheetOpen(false);
    } catch (err) {
      setPendingActionError(t(pendingActionErrorKey(err)));
      dispatch(waiterApi.util.invalidateTags([{ type: 'SessionDetail', id: sessionUuid }]));
    } finally {
      setPendingBusyOrderUuid(null);
    }
  }

  return (
    <div hidden={hidden} data-testid="commandes-view" className={`flex-1 min-h-0 flex-col ${hidden ? 'hidden' : 'flex'}`}>
      <div className="flex-1 min-h-0 overflow-y-auto waiter-scroll p-3">
        {/* D14 — tablet: content column capped at 720px and centred so the
            « − » isn't 600px away from the name on a full-width bar. */}
        <div className="md:max-w-[720px] md:mx-auto space-y-2">
          <p data-testid="commandes-total" aria-live="polite" className="sticky top-0 bg-ash font-mono font-bold text-xl tabular-nums py-1">
            {t('waiter.ticket.commandes.total', { amount: formatPrice(Math.round(parseFloat(sessionDetail?.runningTotal ?? '0') * 100)) })}
          </p>

          {/* DF-0223 — l'onglet Commandes n'offrait AUCUN bouton pour valider
              une tournee de client : le ✔ vert du pied termine la table,
              « Imprimer » sort l'addition, et la seule entree restante etait la
              petite puce orange d'une ligne, stylee comme une etiquette d'etat.
              Mesure Yuji du 2026-09-11 : 4 tournees de client sur 6 sont
              parties en cuisine avec plus de 3 minutes de retard, jusqu'a
              787 s, pendant que le serveur imprimait l'addition. Ce bandeau est
              la premiere chose de l'onglet, au-dessus du reste. */}
          {pendingRounds.length > 0 && (
            <div
              data-testid="commandes-pending-banner"
              className="rounded-xl p-3 shadow-soft flex items-center justify-between gap-2"
              style={{ backgroundColor: 'var(--w-pending-bg)', color: 'var(--w-pending-fg)' }}
            >
              <div className="min-w-0">
                <p className="font-sans text-[18px] font-bold">
                  {t('waiter.ticket.commandes.pendingTitle', { n: pendingRounds.length })}
                </p>
                <p className="font-mono text-[15px]">
                  {t('waiter.ticket.commandes.pendingBody', {
                    n: pendingRounds.reduce((sum, r) => sum + r.itemsCount, 0),
                  })}
                </p>
              </div>
              <button
                type="button"
                data-testid="commandes-pending-open"
                onClick={() => setPendingSheetOpen(true)}
                className="min-w-[104px] h-12 px-3 rounded-btn border-2 border-ink bg-mint font-sans text-[16px] font-bold text-ink shrink-0"
              >
                {t('waiter.ticket.commandes.pendingAction')}
              </button>
            </div>
          )}

          {/* SC-902 — T7: services held from a « Par service » round, one
              row each, « Lancer » sends that round to the kitchen. */}
          {heldRoundRows.length > 0 && (
            <div data-testid="commandes-held-rounds" className="bg-paper rounded-xl p-3 shadow-soft">
              <h3 className="font-sans text-lg font-bold mb-2">{t('waiter.ticket.commandes.heldTitle')}</h3>
              <div className="space-y-2">
                {heldRoundRows.map((row) => (
                  <div
                    key={row.round}
                    data-testid={`commandes-held-round-${row.round}`}
                    className="flex items-center justify-between gap-2 min-h-[52px] py-1.5"
                  >
                    <div className="min-w-0">
                      <p className="font-sans text-[18px] font-semibold">
                        {row.label
                          ? t('waiter.ticket.commandes.heldRoundNamed', { n: row.round, name: row.label })
                          : t('waiter.ticket.commandes.heldRound', { n: row.round })}
                      </p>
                      <p className="font-mono text-[15px] text-ink-mute">
                        {row.dishCount === 1
                          ? t('waiter.ticket.commandes.heldDishesOne', { count: row.dishCount })
                          : t('waiter.ticket.commandes.heldDishesOther', { count: row.dishCount })}
                      </p>
                    </div>
                    <button
                      type="button"
                      data-testid={`commandes-fire-${row.round}`}
                      disabled={fireBusyRound === row.round}
                      onClick={() => setFireTarget(row)}
                      className="min-w-[88px] h-11 px-3 rounded-btn font-sans text-[16px] font-bold text-white shrink-0 disabled:opacity-40"
                      style={{ backgroundColor: 'var(--w-info)' }}
                    >
                      {t('waiter.ticket.commandes.fire')}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {categoryBlocks.length === 0 && mergedQuestionLines.length === 0 && (
            <p className="font-mono text-sm text-ink-mute">{t('waiter.ticket.commandes.empty')}</p>
          )}

          {categoryBlocks.map(({ category, items: catLines }) => {
          const cat = categoriesById.get(category);
          const swatch = categoryColor({ id: category, color: cat?.color });
          const heading = cat?.name ?? t('waiter.ticket.commandes.otherCategory');
          return (
            <div key={category} data-testid={`commandes-cat-${category}`} className="bg-paper rounded-xl p-3 shadow-soft">
              <div className="flex items-center gap-2 mb-2">
                <span aria-hidden="true" className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: swatch }} />
                <h3 className="font-sans text-lg font-bold">{heading}</h3>
              </div>
              <div className="space-y-2">
                {catLines.map((line) => {
                  const first = line.items[0];
                  const qty = line.items.reduce((s, i) => s + i.qte, 0);
                  const locked = line.items.some((i) =>
                    isCommandesLineLocked({
                      onlineStatus: i.onlineStatus,
                      paymentStatus: i.paymentStatus,
                      caisseHandoffStatus: i.caisseHandoffStatus,
                      isCartOption: i.isCartOption,
                    }),
                  );
                  // SC-787 — « En attente » is per ROUND, not per order: the
                  // anchor carries every round of the table and goes back to
                  // pending_validation for the newest one alone.
                  const received = !isRoundPendingValidation({
                    onlineStatus: first.onlineStatus,
                    roundNumber: first.roundNumber,
                    lastValidatedRoundNumber: first.lastValidatedRoundNumber,
                  });
                  // SC-902 — T7: this line's round is a LIVE held round of
                  // its order — the amber « En attente d'envoi » chip
                  // replaces the plain « R{{n}} » one; the − stays available
                  // regardless (retiring a dish from an unsent service is
                  // legitimate).
                  const isHeld = heldRoundsByOrder.get(first.orderUuid)?.has(first.roundNumber) ?? false;
                  // SC-1113 — the line's price. The total is the server's
                  // `total_ttc` (what the table is billed, so the lines add
                  // up to « Total »), never unit × qty recomputed here: under
                  // a promotion the two differ.
                  const lineTotalCents = line.items.reduce((s, i) => s + Math.round(parseFloat(i.totalTtc) * 100), 0);
                  return (
                    <div
                      key={first.uuid}
                      data-testid={`commandes-line-${first.uuid}`}
                      data-qty={qty}
                      data-round={first.roundNumber}
                      data-locked={locked}
                      className="grid grid-cols-[1fr_auto] items-center gap-2 min-h-[52px] py-1.5"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-1 flex-wrap">
                          <DishCodeTag name={first.name} code={undefined} size="sm" />
                          <span className="font-sans text-[18px] font-semibold line-clamp-2">{first.name}</span>
                        </div>
                        {first.options.map((opt, idx) => (
                          <p
                            key={idx}
                            data-testid={`commandes-line-option-${first.uuid}-${idx}`}
                            className="ps-4 font-mono text-[15px] text-ink-mute"
                          >
                            {(opt.name ?? opt.label ?? '') + (opt.qty && opt.qty > 1 ? ` ×${opt.qty}` : '')}
                          </p>
                        ))}
                        {first.comment && (
                          <p data-testid={`commandes-line-comment-${first.uuid}`} className="ps-4 font-mono text-[15px] italic text-ink-mute">
                            {first.comment}
                          </p>
                        )}
                        <p
                          data-testid={`commandes-line-price-${first.uuid}`}
                          className="flex flex-wrap items-baseline gap-x-2 font-mono text-[15px] tabular-nums"
                        >
                          <span className="font-bold whitespace-nowrap">{formatPrice(lineTotalCents)}</span>
                          {qty > 1 && (
                            <span data-testid={`commandes-line-unit-${first.uuid}`} className="text-ink-mute whitespace-nowrap">
                              {t('waiter.ticket.commandes.unitPrice', {
                                n: qty,
                                price: formatPrice(Math.round(parseFloat(first.unitTtc) * 100)),
                              })}
                            </span>
                          )}
                        </p>
                      </div>
                      {/* SC-1113 — the wide status chips sit UNDER « × n · R · − »,
                          not beside them: on one row the cluster took 253 of a
                          360px phone's width on a held line, leaving 51px for the
                          name and its price, which ran into « × n ». */}
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <div className="flex items-center gap-2">
                          {qty > 1 && (
                            <span data-testid={`commandes-line-qty-${first.uuid}`} className="font-mono font-bold text-[18px] tabular-nums">
                              {t('waiter.ticket.commandes.qty', { n: qty })}
                            </span>
                          )}
                          {!isHeld && (
                            <span data-testid={`commandes-line-round-${first.uuid}`} className="font-mono text-[13px] font-bold border rounded-btn-sm px-1.5 py-0.5">
                              {t('waiter.ticket.commandes.round', { n: first.roundNumber })}
                            </span>
                          )}
                          {!locked && (
                            <button
                              type="button"
                              data-testid={`commandes-minus-${first.uuid}`}
                              aria-label={t('waiter.ticket.commandes.minusAria', { name: first.name })}
                              onClick={() => openDecrementConfirm(line)}
                              className="w-12 h-12 rounded-full flex items-center justify-center text-white text-2xl"
                              style={{ backgroundColor: 'var(--w-danger)' }}
                            >
                              −
                            </button>
                          )}
                        </div>
                        {isHeld && (
                          <span
                            data-testid={`commandes-line-held-${first.uuid}`}
                            className="font-mono text-[13px] font-bold rounded-btn-sm px-1.5 py-0.5"
                            style={{ backgroundColor: 'var(--w-pending-bg)', color: 'var(--w-pending-fg)' }}
                          >
                            {t('waiter.ticket.commandes.heldChip')}
                          </span>
                        )}
                        {!received && (
                          <button
                            type="button"
                            data-testid={`commandes-line-status-${first.uuid}`}
                            onClick={() => setPendingSheetOpen(true)}
                            className="font-mono text-[13px] font-bold rounded-btn-sm px-1.5 py-0.5"
                            style={{ backgroundColor: 'var(--w-pending-bg)', color: 'var(--w-pending-fg)' }}
                          >
                            {t('waiter.ticket.commandes.statusPending')}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* D4 — cart-scope question answers (SC-739/SC-753 « cart_option »
            lines) never render as dish lines: no code tag, no round chip,
            no « − ». They sit in their own block after every category. */}
        {mergedQuestionLines.length > 0 && (
          <div data-testid="commandes-questions" className="bg-paper rounded-xl p-3 shadow-soft">
            <h3 className="font-sans text-lg font-bold mb-2">{t('waiter.ticket.commandes.questionsHeading')}</h3>
            <div className="space-y-2">
              {mergedQuestionLines.map((line) => {
                const first = line.items[0];
                const qty = line.items.reduce((s, i) => s + i.qte, 0);
                return (
                  <div
                    key={first.uuid}
                    data-testid={`commandes-question-${first.uuid}`}
                    data-qty={qty}
                    className="min-h-[52px] flex items-center gap-2 py-1.5"
                  >
                    <span className="font-sans text-[18px] font-semibold line-clamp-2 flex-1">
                      {t('waiter.ticket.commandes.questionAnswer', { name: first.name })}
                    </span>
                    {qty > 1 && (
                      <span
                        data-testid={`commandes-question-qty-${first.uuid}`}
                        className="font-mono font-bold text-[18px] tabular-nums shrink-0"
                      >
                        {t('waiter.ticket.commandes.qty', { n: qty })}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* §2.7 — editing a SENT line is out of scope (§6): this hint tells
            the waiter the workaround (− then re-add in Plats). Only once
            there is at least one sent dish line to point at. */}
        {dishItems.length > 0 && (
          <p data-testid="commandes-edit-hint" className="font-mono text-[14px] text-ink-mute">
            {t('waiter.ticket.commandes.editHint')}
          </p>
        )}
        </div>
      </div>

      {decrementTarget && (
        <div
          data-testid="decrement-confirm"
          role="alertdialog"
          aria-modal="true"
          className="fixed inset-0 z-[1190] flex items-center justify-center p-4"
          style={{ backgroundColor: 'var(--w-scrim)' }}
        >
          <div className="bg-paper border-2 border-ink rounded-btn p-5 max-w-sm w-full flex flex-col gap-4">
            <p className="font-sans text-lg">{t('waiter.ticket.commandes.decrementConfirm.title')}</p>
            <div className="flex gap-3">
              <button
                type="button"
                data-testid="decrement-confirm-cancel"
                disabled={decrementSubmitting}
                onClick={() => setDecrementTarget(null)}
                className="h-12 flex-1 border-2 border-ink rounded-btn font-sans text-[18px] font-bold disabled:opacity-40"
              >
                {t('waiter.ticket.commandes.decrementConfirm.cancel')}
              </button>
              <button
                type="button"
                data-testid="decrement-confirm-submit"
                disabled={decrementSubmitting}
                onClick={confirmDecrement}
                className="h-12 flex-1 rounded-btn font-sans text-[18px] font-bold text-white disabled:opacity-40"
                style={{ backgroundColor: 'var(--w-danger)' }}
              >
                {t('waiter.ticket.commandes.decrementConfirm.submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {fireTarget && (
        <TicketFireConfirm
          target={fireTarget}
          heldRoundName={heldRoundName}
          submitting={fireSubmitting}
          onCancel={() => setFireTarget(null)}
          onConfirm={() => void confirmFire()}
        />
      )}

      <PendingRoundsSheet
        open={pendingSheetOpen}
        onClose={() => setPendingSheetOpen(false)}
        tableNumber={label}
        rounds={pendingRounds}
        onValidate={(round) => void handleValidate(round)}
        onRefuse={(round) => void handleRefuse(round)}
        onModify={(round) => void handleModify(round)}
        busyOrderUuid={pendingBusyOrderUuid}
        error={pendingActionError}
      />
    </div>
  );
}
